import { FILTER_FIELDS, FILTER_OPTIONS } from '_constants/old/filters';
import { dropEmptyFilterSections } from 'modules/Modals/old/FilterModal/utils/dropEmptyFilterSections';

// P20M-1308 — hides the cigarettes/cigars/e-cig/smokeless + Alcohol rows of Products sold, Store
// ownership, and the WIC/SNAP/pharmacy/internet rows of Other retailer details unless the user has
// the matching permission. AVC is gated separately (AZ/FL). The general "Sells tobacco" question,
// Store type, Proximity, residential and ATCC rows are not part of the hide list, so they stay
// as-is. (Alcohol is gated here per an explicit product decision, even though the visit-list
// section of the spec doc lists only the Tobacco rows.)
export const getEnforcementRetailerCharacteristics = (
  corpOptions,
  isVaAccount,
  hasResidentialModule,
  isMdAccount,
  isAzAccount,
  isAssesment,
  {
    canViewProductsSoldTobacco = false,
    canViewProductsSoldAlcohol = false,
    canViewStoreOwnership = false,
    canViewOtherRetailerDetails = false,
    canViewAvc = false,
  } = {},
) =>
  // Drop any multiple/double group whose rows are all hidden, so we never render an empty
  // collapsible section (e.g. Products sold once Tobacco/Alcohol are hidden).
  dropEmptyFilterSections([
    {
      id: 1,
      field: FILTER_FIELDS.IS_CURRENT,
      title: 'Open for business',
      options: FILTER_OPTIONS.BUSINESS_OPTIONS,
      type: 'single',
    },
    {
      type: 'multiple',
      title: 'Products sold',
      id: 2,
      collapsed: [
        // The general "Sells tobacco" question is NOT in the P20M-1308 hide list (only the
        // cigarettes/cigars/e-cig/smokeless rows are), so it stays visible like Alcohol below.
        {
          id: 3,
          title: 'Tobacco',
          field: FILTER_FIELDS.SELLS_TOBACCO,
          options: FILTER_OPTIONS.SELLS_TOBACCO_OPTIONS,
        },
        !isAzAccount &&
          canViewProductsSoldTobacco && {
            id: 4,
            title: 'Sells cigarettes',
            field: FILTER_FIELDS.RETAILER_CIGARETTES_SOLD,
            options: FILTER_OPTIONS.SELLS_CIGARETTES_OPTIONS,
            isSubcategory: true,
          },
        !isAzAccount &&
          canViewProductsSoldTobacco && {
            id: 5,
            title: 'Sells cigars',
            field: FILTER_FIELDS.RETAILER_CIGAR_SOLD,
            options: FILTER_OPTIONS.SELLS_CIGARS_OPTIONS,
            isSubcategory: true,
          },
        !isAzAccount &&
          canViewProductsSoldTobacco && {
            id: 6,
            title: 'Sells e-cigarettes',
            field: FILTER_FIELDS.RETAILER_ECIG_SOLD,
            options: FILTER_OPTIONS.SELLS_ECIG_OPTIONS,
            isSubcategory: true,
          },
        !isAzAccount &&
          canViewProductsSoldTobacco && {
            id: 7,
            title: 'Sells smokeless tobacco products',
            field: FILTER_FIELDS.RETAILER_SMOKELESS_SOLD,
            options: FILTER_OPTIONS.SELLS_SMOKELESS_OPTIONS,
            isSubcategory: true,
          },
        !isAzAccount &&
          canViewProductsSoldAlcohol && {
            id: 8,
            title: 'Alcohol',
            field: FILTER_FIELDS.SELLS_ALCOHOL,
            options: FILTER_OPTIONS.SELLS_ALCOHOL_OPTIONS,
          },
      ].filter(Boolean),
    },
    isVaAccount && {
      type: 'multiple',
      title: 'Gambling',
      id: 9,
      collapsed: [
        {
          id: 10,
          title: 'Sells lottery tickets',
          field: FILTER_FIELDS.RETAILER_TICKETS_SOLD,
          options: FILTER_OPTIONS.SELLS_TICKETS_OPTIONS,
        },
        {
          id: 11,
          title: 'Has gaming machines',
          field: FILTER_FIELDS.RETAILER_GAMING_MACHINE,
          options: FILTER_OPTIONS.HAS_GAMING_MACHINES_OPTIONS,
        },
      ],
    },
    {
      id: 12,
      field: FILTER_FIELDS.RETAILER_BUSINESS_TYPE,
      title: 'Store type',
      options: FILTER_OPTIONS.STORE_TYPE_OPTIONS,
      type: 'single',
    },
    {
      type: 'double',
      title: 'Store ownership',
      id: 13,
      // The store-ownership permission (or master override) gates the rows, not the
      // section header, so dropEmptyFilterSections removes the whole section when nothing
      // inside is visible. Corporation is also dropped when the account has no
      // corporation data, so the header never renders with an empty checkbox list.
      // The AZ account is not special-cased here: Store ownership follows the same
      // permission as every other account.
      collapsed: [
        canViewStoreOwnership && {
          id: 14,
          field: FILTER_FIELDS.CHAIN,
          options: FILTER_OPTIONS.CHAIN_OPTIONS,
        },
        canViewStoreOwnership &&
          corpOptions?.length > 0 && {
            id: 15,
            field: FILTER_FIELDS.CORPORATION,
            options: corpOptions,
          },
      ].filter(Boolean),
    },
    {
      type: 'proximity',
      title: 'Proximity',
      id: 16,
    },
    {
      type: 'double',
      title: 'Other retailer details',
      id: 19,
      collapsed: [
        !isAzAccount &&
          canViewOtherRetailerDetails && {
            id: 20,
            field: FILTER_FIELDS.WIC,
            options: FILTER_OPTIONS.WIC_OPTIONS,
          },
        !isAzAccount &&
          canViewOtherRetailerDetails && {
            id: 21,
            field: FILTER_FIELDS.SNAP,
            options: FILTER_OPTIONS.SNAP_OPTIONS,
          },
        !isAzAccount &&
          canViewOtherRetailerDetails && {
            id: 22,
            field: FILTER_FIELDS.PHARMACY,
            options: FILTER_OPTIONS.HAS_PHARMACY_OPTIONS,
          },
        canViewAvc && {
          id: 23,
          field: FILTER_FIELDS.HAS_AVC_AGREEMENT,
          options: FILTER_OPTIONS.HAS_AVC_AGREEMENT_OPTIONS,
        },
        !isAzAccount &&
          canViewOtherRetailerDetails && {
            id: 24,
            field: FILTER_FIELDS.INTERNET_SALES_VISTIS,
            options: FILTER_OPTIONS.INTERNET_SALES_OPTIONS,
          },
        hasResidentialModule && {
          id: 25,
          field: FILTER_FIELDS.RESIDENTIAL_LOCATION,
          options: FILTER_OPTIONS.RESIDENTIAL_LOCATION_OPTIONS,
        },
        isMdAccount && {
          id: 26,
          field: FILTER_FIELDS.ATCC,
          options: FILTER_OPTIONS.ATCC_OPTIONS,
        },
        isMdAccount && {
          id: 31,
          field: FILTER_FIELDS.HAS_CONTROL_NUMBER,
          options: FILTER_OPTIONS.HAS_CONTROL_NUMBER_OPTIONS,
        },
      ].filter(Boolean),
    },
    isVaAccount &&
      isAssesment && {
        type: 'multiple',
        title: 'Hemp-Derived Cannabis Products',
        id: 27,
        collapsed: [
          {
            id: 28,
            title: 'Sales Status',
            field: FILTER_FIELDS.RETAILER_SELLS_HEMP,
            options: FILTER_OPTIONS.SELLS_HEMP_OPTIONS,
          },
          {
            id: 29,
            title: 'Product Types',
            field: FILTER_FIELDS.RETAILER_SELLS_HEMP_TYPES,
            options: FILTER_OPTIONS.SELLS_HEMP_TYPES_OPTIONS,
          },
          {
            id: 30,
            title: 'Outdoor Advertising',
            field: FILTER_FIELDS.RETAILER_HAS_OUTDOOR_AD_HEMP,
            options: FILTER_OPTIONS.HAS_OUTDOOR_AD_HEMP_OPTIONS,
          },
        ],
      },
  ]);
